import { type ReactNode, type Ref, useRef } from 'react'
import cx from 'classnames'
import {
  Table as AriaTable,
  TableHeader as AriaTableHeader,
  Column as AriaColumn,
  Row as AriaRow,
  TableBody as AriaTableBody,
  Cell as AriaCell,
} from 'react-aria-components'
import {
  AutoSkeleton,
  IsSkeletonContext,
  useIsSkeleton,
} from '@/components/Skeleton'
import { match } from '@/utils/pattern-match-util'
import { type Observable } from 'rxjs'
import { useObservable } from '@/hooks/rx-hooks'
import styles from './Table.module.css'

/**
 * Defines spacing between rows.
 */
export type RowSpacing = 'base' | 'lg'

export interface TableProps {
  children?: ReactNode
  'aria-label'?: string
  'aria-labelledby'?: string

  /**
   * Spacing to use between rows.
   */
  rowSpacing?: RowSpacing

  /**
   * The variant to use. When `full`, the cells receive horizontal padding
   * and the header border connects with elements on the `x` axis.
   */
  variant?: 'default' | 'full' | 'dialog'

  /**
   * Whether to add some trailing spacing to the bottom of the table.
   */
  trailingSpacing?: boolean

  /**
   * Test ID attribute.
   */
  testId?: string
}

/**
 * Table component
 */
export function Table({
  children,
  trailingSpacing,
  variant = 'default',
  rowSpacing = 'base',
  testId,
  ...rest
}: TableProps) {
  return (
    <AriaTable
      data-test={testId}
      className={cx(
        styles.root,
        variant === 'full' && styles.full,
        variant === 'dialog' && styles.dialog,
        trailingSpacing && styles.trailingSpacing,
        match(rowSpacing, {
          base: styles.rowSpacingBase,
          lg: styles.rowSpacingLg,
        }),
      )}
      {...rest}
    >
      {children}
    </AriaTable>
  )
}

interface TableHeaderProps {
  children?: ReactNode
  isSkeleton?: boolean
  isBorderless?: boolean
  sticky?: number | string
  ref?: Ref<HTMLTableSectionElement>
  testId?: string
}

/**
 * Table Header
 */
export function TableHeader({
  children,
  isSkeleton,
  isBorderless,
  sticky,
  ref,
  testId,
}: TableHeaderProps) {
  return (
    <IsSkeletonContext value={isSkeleton}>
      <AriaTableHeader
        ref={ref}
        data-test={testId}
        style={{ top: sticky }}
        className={cx(
          styles.header,
          sticky !== undefined && styles.sticky,
          isBorderless && styles.borderless,
        )}
      >
        {children}
      </AriaTableHeader>
    </IsSkeletonContext>
  )
}

interface TableColumnProps {
  children?: ReactNode
  isRowHeader?: boolean
  allowsSorting?: boolean
  isSkeleton?: boolean
  id?: string
  textValue?: string
  align?: TableCellAlign
  minWidth?: number | string
  maxWidth?: number | string
  width?: number | string

  /**
   * Whether this column represents the actions column, meaning it
   * won't render a title.
   */
  isRowActionsHeader?: boolean

  /**
   * Test ID attribute.
   */
  testId?: string
}

/**
 * Table Column
 */
export function TableColumn({
  align,
  isRowActionsHeader,
  isSkeleton,
  children,
  width,
  minWidth,
  maxWidth,
  testId,
  ...rest
}: TableColumnProps) {
  isSkeleton = useIsSkeleton(isSkeleton)
  return (
    <AriaColumn
      textValue={isRowActionsHeader ? 'Actions' : undefined}
      style={{ width, minWidth, maxWidth }}
      data-test={testId}
      {...rest}
      className={({ isFocusVisible }) =>
        cx(
          styles.column,
          isRowActionsHeader && styles.actionsColumn,
          isFocusVisible && styles.focusVisible,
          getTableCellAlignClassName(align),
        )
      }
    >
      {isRowActionsHeader ? (
        children || <span className="sr-only">Actions</span>
      ) : (
        <AutoSkeleton isSkeleton={isSkeleton}>{children}</AutoSkeleton>
      )}
    </AriaColumn>
  )
}

interface TableBodyProps {
  children?: ReactNode
  focusFirst$?: Observable<unknown>

  /**
   * Empty state to render when there are no rows in the table.
   */
  emptyState?: ReactNode

  /**
   * Test ID attribute.
   */
  testId?: string
}

/**
 * Table Body
 */
export function TableBody({
  children,
  emptyState,
  focusFirst$,
  testId,
}: TableBodyProps) {
  const ref = useRef<HTMLTableSectionElement>(null)
  useObservable(focusFirst$, () => {
    ref.current?.querySelector('tr')?.focus()
  })
  return (
    <AriaTableBody
      ref={ref}
      data-test={testId}
      className={styles.body}
      renderEmptyState={() => emptyState}
    >
      {children}
    </AriaTableBody>
  )
}

interface TableRowProps {
  children?: ReactNode
  isDisabled?: boolean
  textValue?: string
  ref?: Ref<HTMLTableRowElement>
  testId?: string
  isHighlighted?: boolean
}

/**
 * Table Row
 */
export function TableRow({ children, ref, testId, isHighlighted, ...rest }: TableRowProps) {
  return (
    <AriaRow
      data-test={testId}
      {...rest}
      className={({ isFocusVisible }) =>
        cx(styles.row, isFocusVisible && styles.focusVisible, isHighlighted && styles.highlighted)
      }
      ref={ref}
    >
      {children}
    </AriaRow>
  )
}

interface TableCellProps {
  children?: ReactNode
  align?: TableCellAlign

  /**
   * Whether this cell represents the actions cell,
   * meaning it'll render some form of interactable element rather
   * than static content.
   */
  isActions?: boolean

  /**
   * Test ID attribute.
   */
  testId?: string
}

/**
 * Table Cell
 */
export function TableCell({
  align,
  isActions,
  children,
  testId,
  ...rest
}: TableCellProps) {
  return (
    <AriaCell
      data-test={testId}
      textValue={isActions ? 'Actions' : undefined}
      {...rest}
      className={({ isFocusVisible }) =>
        cx(
          styles.cell,
          isActions && styles.actionsCell,
          isFocusVisible && styles.focusVisible,
          getTableCellAlignClassName(align),
        )
      }
    >
      {children}
    </AriaCell>
  )
}

interface TableRowSkeletonProps {
  children?: ReactNode
  ref?: Ref<HTMLTableRowElement>
}

/**
 * Table Row Skeleton
 */
export function TableRowSkeleton({ ref, children }: TableRowSkeletonProps) {
  return (
    <TableRow isDisabled textValue="Placeholder" ref={ref}>
      {children}
    </TableRow>
  )
}

type TableCellSkeletonProps = TableCellProps

/**
 * Table Cell Skeleton
 */
export function TableCellSkeleton({
  children,
  ...rest
}: TableCellSkeletonProps) {
  return (
    <TableCell {...rest}>
      <IsSkeletonContext value={true}>{children}</IsSkeletonContext>
    </TableCell>
  )
}

type TableCellAlign = 'left' | 'center' | 'right'

function getTableCellAlignClassName(align: TableCellAlign | undefined): string {
  return match(align ?? 'left', {
    left: 'text-left',
    center: 'text-center',
    right: 'text-right',
  })
}
